import { v4 as uuidv4 } from 'uuid';
import {
  TempeFabricationPublicClient,
  TempeFabricationPublicServiceException
} from "@tempefab-model/tempe-fabrication-client";

const SESSION_ID_KEY = 'tempefab_session_id';
const USER_ID_KEY = 'tempefab_user_id';

/**
 * Get or create a session ID for attribution tracking
 * Session ID is stored in sessionStorage (persists across page refreshes, cleared when tab closes)
 * @returns Session ID (UUID)
 */
function getOrCreateSessionId(): string {
  // Try to get existing session ID from sessionStorage
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);

  if (!sessionId) {
    // Generate new session ID
    sessionId = uuidv4();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    console.log('[SessionClient] Created new session ID:', sessionId);
  }

  return sessionId;
}

/**
 * Clear the current session ID (useful for testing or explicit session resets)
 */
export function clearSessionId(): void {
  sessionStorage.removeItem(SESSION_ID_KEY);
  console.log('[SessionClient] Session ID cleared');
}

/**
 * Get the current session ID without creating a new one
 * @returns Session ID or null if not set
 */
export function getCurrentSessionId(): string | null {
  return sessionStorage.getItem(SESSION_ID_KEY);
}

/**
 * Get or create a user ID for persistent user tracking
 * User ID is stored in localStorage (persists across sessions and browser restarts)
 * @returns User ID (UUID)
 */
function getOrCreateUserId(): string {
  // Try to get existing user ID from localStorage
  let userId = localStorage.getItem(USER_ID_KEY);

  if (!userId) {
    // Generate new user ID
    userId = uuidv4();
    localStorage.setItem(USER_ID_KEY, userId);
    console.log('[SessionClient] Created new user ID:', userId);
  }

  return userId;
}

/**
 * Clear the current user ID (useful for testing or explicit user resets)
 */
export function clearUserId(): void {
  localStorage.removeItem(USER_ID_KEY);
  console.log('[SessionClient] User ID cleared');
}

/**
 * Get the current user ID without creating a new one
 * @returns User ID or null if not set
 */
export function getCurrentUserId(): string | null {
  return localStorage.getItem(USER_ID_KEY);
}

/**
 * Middleware to add X-Session-ID and X-User-ID headers to all API requests
 * This enables both session-based and persistent user attribution tracking
 */
const addTrackingHeadersMiddleware = (next: any, context: any) => async (args: any) => {
  const sessionId = getOrCreateSessionId();
  const userId = getOrCreateUserId();

  // Add tracking headers to the HTTP request
  if (args.request) {
    args.request.headers = args.request.headers || {};
    args.request.headers['x-session-id'] = sessionId;
    args.request.headers['x-user-id'] = userId;
  }

  return next(args);
};

/**
 * Create and configure TempeFabrication client with session and user tracking
 */
function createSessionClient(): TempeFabricationPublicClient {
  const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
  if (!apiEndpoint) {
    console.error("Missing REACT_APP_API_ENDPOINT environment variable");
  }

  const client = new TempeFabricationPublicClient({
    endpoint: apiEndpoint || "",
    region: "us-west-2"
  });

  // Add middleware to inject tracking headers (session ID and user ID)
  client.middlewareStack.add(addTrackingHeadersMiddleware, {
    step: 'build',
    name: 'addTrackingHeadersMiddleware',
    priority: 'high', // Ensure this runs early in the middleware stack
  });

  console.log('[SessionClient] Client initialized with session and user tracking middleware');

  return client;
}

// Export singleton client instance with session tracking
export const sessionClient = createSessionClient();

// Re-export exception type for convenience
export { TempeFabricationPublicServiceException };
